// ============================================================================
// Cargo grouping for the contents modal (and its PDF).
//
// A consolidated truck carries POs belonging to MANY different customer orders —
// booking 3783 rides 37 POs across 21 leads. Listed flat, that reads as one long
// undifferentiated wall, which is how a loader hands a client's boxes to the wrong
// drop. So the contents are grouped one level UP first — by the ORDER the PO
// belongs to (lead id + client name) — and only then, within a group, listed by PO
// number ascending, which is the order the paperwork is stacked in.
//
// Each PO is filed under ITS OWN order, never the clicked booking's: attributing
// the truck's cargo to the row you happened to open makes a one-PO order look like
// a 37-PO one. A PO whose order we could not resolve stays visible in its own
// un-attributed group rather than being filed under someone else's name.
// ============================================================================
import { LogisticsCargoAllocation, PoOrderContext } from "../logistics.api";

export interface AllocationGroup {
  key: string;
  leadId: string | null;
  clientName: string | null;
  label: string; // "ENQ2026082888849 · Ruthvik" — the group heading, ready to render
  allocations: LogisticsCargoAllocation[];
}

// PO numbers are digit-heavy ("MD8202683250"), so compare NUMERICALLY — a plain
// string sort puts MD…3411 before MD…342 and the stack no longer reads in order.
const compareText = (a: string | null, b: string | null): number => {
  if (!a && !b) return 0;
  if (!a) return 1; // blanks sink to the bottom of their level
  if (!b) return -1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
};

const groupLabel = (leadId: string | null, clientName: string | null): string => {
  const parts = [leadId, clientName].filter(Boolean) as string[];
  return parts.length ? parts.join(" · ") : "Order not identified";
};

// `contexts` is keyed by PO number — what `lookupPoOrderContexts` resolved. An allocation that
// carries its own lead/client (once the cargo serializer exposes them) is trusted first and needs
// no lookup at all.
export function groupAllocations(
  allocations: LogisticsCargoAllocation[],
  contexts: Record<string, PoOrderContext> = {}
): AllocationGroup[] {
  const groups = new Map<string, AllocationGroup>();

  for (const alloc of allocations ?? []) {
    const ctx = alloc.po_number ? contexts[alloc.po_number] : undefined;
    const leadId = alloc.lead_id ?? ctx?.lead_id ?? null;
    const clientName = alloc.client_name ?? ctx?.client_name ?? null;
    const key = `${leadId ?? ""}||${clientName ?? ""}`;
    const existing = groups.get(key);
    if (existing) existing.allocations.push(alloc);
    else
      groups.set(key, {
        key,
        leadId,
        clientName,
        label: groupLabel(leadId, clientName),
        allocations: [alloc],
      });
  }

  return Array.from(groups.values())
    .map((g) => ({
      ...g,
      allocations: [...g.allocations].sort((a, b) =>
        compareText(a.po_number, b.po_number)
      ),
    }))
    .sort(
      (a, b) =>
        compareText(a.leadId, b.leadId) || compareText(a.clientName, b.clientName)
    );
}
